import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Factory, Shirt, Sparkles, Truck, ExternalLink } from "lucide-react";
import PageMeta from "../components/PageMeta";

const PANTHER_URL = "https://www.mypanther.fr";

export default function Panther() {
    return (
        <div className="bg-[#09090b] text-zinc-100">
            <PageMeta
                title="Panther — Notre partenaire fabricant | Sponsormania.fr"
                description="Découvrez Panther, la marque française qui fabrique les maillots des clubs financés sur Sponsormania.fr. Impression HD, textile sportif, savoir-faire artisanal."
            />

            {/* HERO */}
            <section className="border-b border-zinc-900 px-6 lg:px-8 pt-20 pb-24 relative overflow-hidden">
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-red-700/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative max-w-7xl mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-4">
                        Notre partenaire fabricant
                    </div>
                    <h1 className="font-display text-6xl sm:text-7xl lg:text-9xl uppercase leading-[0.85] tracking-tighter">
                        <span className="text-orange-500">Panther</span>.<br />
                        Le maillot,<br />leur métier.
                    </h1>
                    <p className="mt-8 max-w-3xl text-lg text-zinc-300">
                        Derrière chaque maillot financé sur Sponsormania.fr, il y a{" "}
                        <span className="text-white font-bold">Panther</span> — une marque
                        indépendante spécialisée dans le textile sportif haut de gamme. Impression
                        HD, coupes techniques, personnalisation illimitée : Panther transforme le
                        soutien de vos sponsors en pièce d&apos;équipement digne du haut niveau.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <a
                            href={PANTHER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="panther-hero-visit"
                            className="btn-primary"
                        >
                            <ExternalLink size={18} /> Découvrir mypanther.fr
                        </a>
                        <Link to="/register" data-testid="panther-hero-register" className="btn-ghost">
                            <Shirt size={18} /> Financer mon maillot
                        </Link>
                    </div>
                </div>
            </section>

            {/* PILIERS */}
            <section className="px-6 lg:px-8 py-20 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto">
                    <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-10">
                        Un savoir-faire<br className="hidden md:block" /> au service du sport amateur.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {PILLARS.map((p) => (
                            <div key={p.title} className="card-tu">
                                <div className="text-orange-500 mb-3">{p.icon}</div>
                                <h3 className="font-display text-2xl uppercase tracking-tight">
                                    {p.title}
                                </h3>
                                <p className="text-zinc-400 mt-2 text-sm leading-relaxed">
                                    {p.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY THEM */}
            <section className="px-6 lg:px-8 py-20 border-b border-zinc-900">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-3">
                            Pourquoi Panther&nbsp;?
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight leading-[0.9]">
                            Parce qu&apos;un maillot,<br />ce n&apos;est pas qu&apos;un vêtement.
                        </h2>
                    </div>
                    <div className="space-y-6 text-zinc-300">
                        <p>
                            Panther partage la conviction qui fonde Sponsormania.fr : chaque
                            club amateur mérite un équipement aussi soigné que ceux des pros.
                            Une équipe U11 doit pouvoir arborer un maillot dont elle est fière.
                        </p>
                        <p>
                            Pas de sous-traitance opaque, pas de compromis sur la qualité. Chaque
                            maillot est produit sur commande, contrôlé pièce par pièce, expédié
                            directement au club. Aucun stock inutile, aucun surcoût pour vos
                            sponsors — 100% de l&apos;argent collecté part dans le textile.
                        </p>
                        <p className="text-white font-bold">
                            C&apos;est ce partenariat qui rend possible notre promesse&nbsp;:
                            <span className="text-orange-500"> 0% de commission, 100% de qualité.</span>
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 lg:px-8 py-24">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tight">
                        Envie de voir leur univers&nbsp;?
                    </h2>
                    <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
                        Explorez la collection Panther, leurs finitions, leurs projets clubs, et
                        contactez-les directement pour tout besoin sur-mesure hors plateforme.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <a
                            href={PANTHER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="panther-footer-visit"
                            className="btn-primary"
                        >
                            <ExternalLink size={18} /> Visiter mypanther.fr <ArrowRight size={16} />
                        </a>
                        <Link to="/concept" data-testid="panther-footer-concept" className="btn-ghost">
                            Comprendre le concept
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

const PILLARS = [
    {
        title: "Impression HD",
        desc: "Sublimation numérique haute définition. Vos logos restent nets lavage après lavage, saison après saison.",
        icon: <Sparkles size={28} />,
    },
    {
        title: "Textile sportif",
        desc: "Maille respirante, coupes techniques adaptées à chaque discipline — foot, hand, rugby, basket.",
        icon: <Shirt size={28} />,
    },
    {
        title: "Fabrication à la demande",
        desc: "Zéro invendu, zéro gaspillage. Chaque maillot est fabriqué après validation du club.",
        icon: <Factory size={28} />,
    },
    {
        title: "Livraison directe",
        desc: "Expédition au club une fois la production terminée. Suivi transparent à chaque étape.",
        icon: <Truck size={28} />,
    },
];
